import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Mail, Lock, User, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.92l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.27a7.2 7.2 0 0 1 0-4.54v-3.1H1.29a12 12 0 0 0 0 10.74l3.98-3.1z" />
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.63l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z" />
  </svg>
);

// Free account dialog — no payments. Accounts exist to keep each person's
// health history private, persistent and accessible across their devices.
export default function AuthDialog() {
  const { authOpen, closeAuth, googleLogin, login, signup, afterAuth } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (mode === "signup") {
        await signup(name.trim(), email.trim(), password);
      } else {
        await login(email.trim(), password);
      }
      closeAuth();
      toast.success(mode === "signup" ? "Account created — your history is now saved securely" : "Welcome back!");
      await afterAuth();
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={(o) => !o && closeAuth()}>
      <DialogPortal>
        <DialogOverlay className="bg-black/80 backdrop-blur-md z-[60]" />
        <DialogContent
          data-testid="auth-dialog"
          className="fixed left-[50%] top-[50%] z-[60] w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 p-0 border-0 bg-transparent shadow-none [&>button.absolute]:hidden"
        >
          <div className="relative w-full overflow-hidden rounded-3xl glass-card border border-white/10 bg-[#05050A]/95 shadow-[0_40px_120px_rgba(0,229,255,0.25)] p-7">
            <DialogTitle className="font-display text-2xl font-semibold tracking-tight text-white">
              {mode === "signup" ? "Create your free account" : "Welcome back"}
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-zinc-400">
              Free forever — an account keeps your health results saved,
              private to you, and available on any of your devices.
            </DialogDescription>

            <button
              data-testid="google-signin-btn"
              onClick={googleLogin}
              className="mt-6 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-white text-zinc-900 px-5 py-3 text-sm font-semibold hover:bg-zinc-100 transition-colors"
            >
              <GoogleMark />
              Continue with Google
            </button>

            <div className="mt-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-zinc-600">
              <div className="flex-1 h-px bg-white/10" />
              or with email
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <form onSubmit={submit} className="mt-5 space-y-3">
              {mode === "signup" && (
                <div className="flex items-center gap-3 rounded-2xl bg-black/40 border border-white/10 focus-within:border-cyan-400/50 px-4 py-3 transition-colors">
                  <User className="w-4 h-4 text-zinc-500 shrink-0" />
                  <input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600"
                  />
                </div>
              )}
              <div className="flex items-center gap-3 rounded-2xl bg-black/40 border border-white/10 focus-within:border-cyan-400/50 px-4 py-3 transition-colors">
                <Mail className="w-4 h-4 text-zinc-500 shrink-0" />
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600"
                />
              </div>
              <div className="flex items-center gap-3 rounded-2xl bg-black/40 border border-white/10 focus-within:border-cyan-400/50 px-4 py-3 transition-colors">
                <Lock className="w-4 h-4 text-zinc-500 shrink-0" />
                <input
                  data-testid="auth-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"}
                  required
                  minLength={mode === "signup" ? 8 : 1}
                  className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600"
                />
              </div>

              {error && (
                <div
                  data-testid="auth-error"
                  className="rounded-xl bg-rose-500/10 border border-rose-400/30 px-4 py-2.5 text-xs text-rose-200"
                >
                  {error}
                </div>
              )}

              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full btn-glow text-white px-5 py-3 text-sm font-medium disabled:opacity-50"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === "signup" ? "Create free account" : "Sign in"}
              </button>
            </form>

            <button
              data-testid="auth-mode-toggle"
              onClick={() => {
                setMode((m) => (m === "signin" ? "signup" : "signin"));
                setError("");
              }}
              className="mt-4 w-full text-center text-xs text-zinc-400 hover:text-cyan-300 transition-colors"
            >
              {mode === "signin" ? "New here? Create a free account" : "Already have an account? Sign in"}
            </button>

            <div className="mt-5 flex items-start gap-2 text-[10px] text-zinc-500 leading-relaxed">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-400" />
              Your health data is stored securely under your account only. No
              payments, no ads, no sharing — and you can delete everything
              anytime from Privacy &amp; Data.
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
